import React, { useState, useEffect } from 'react';

const LobbyWaitingRoom = ({ 
  lobbyCode, 
  players, 
  questionCount, 
  category, 
  difficulty, 
  isHost, 
  onStartGame, 
  onLeaveLobby, 
  loading, 
  error,
  onRefreshLobby
}) => {
  const [refreshInterval, setRefreshInterval] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);

  // Log component lifecycle
  useEffect(() => {
    console.log('[LobbyWaitingRoom] Component mounted/updated', {
      lobbyCode,
      playersCount: players?.length || 0,
      isHost,
      loading,
      hasError: !!error
    });
  }, [lobbyCode, players, isHost, loading, error]);

  // Set up auto-refresh for lobby status
  useEffect(() => {
    if (lobbyCode && onRefreshLobby) {
      console.log('[LobbyWaitingRoom] Setting up auto-refresh for lobby:', lobbyCode);
      
      // Refresh immediately
      onRefreshLobby();
      
      // Set up interval to refresh every 2 seconds
      const interval = setInterval(() => {
        console.log('[LobbyWaitingRoom] Auto-refreshing lobby status');
        onRefreshLobby();
      }, 2000);
      
      setRefreshInterval(interval);
      
      // Cleanup interval on unmount
      return () => {
        console.log('[LobbyWaitingRoom] Cleaning up refresh interval');
        clearInterval(interval);
      };
    }
  }, [lobbyCode, onRefreshLobby]);

  const handleStartGame = () => {
    console.log('[LobbyWaitingRoom] Starting game');
    onStartGame();
  };

  const handleCopyLobbyCode = async () => {
    try {
      await navigator.clipboard.writeText(lobbyCode);
      console.log('[LobbyWaitingRoom] Lobby code copied to clipboard:', lobbyCode);
      setCopySuccess(true);
      
      // Reset the success state after 2 seconds
      setTimeout(() => {
        setCopySuccess(false);
      }, 2000);
    } catch (err) {
      console.error('[LobbyWaitingRoom] Failed to copy lobby code:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = lobbyCode;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      
      setCopySuccess(true);
      setTimeout(() => {
        setCopySuccess(false);
      }, 2000);
    }
  };

  const categories = [
    { id: 9, name: "General Knowledge" },
    { id: 17, name: "Science & Nature" },
    { id: 18, name: "Science: Computers" },
    { id: 19, name: "Science: Mathematics" },
    { id: 20, name: "Mythology" },
    { id: 21, name: "Sports" },
    { id: 22, name: "Geography" },
    { id: 23, name: "History" },
    { id: 24, name: "Politics" },
    { id: 25, name: "Art" },
    { id: 26, name: "Celebrities" },
    { id: 27, name: "Animals" },
    { id: 28, name: "Vehicles" }
  ];

  const categoryName = categories.find(cat => cat.id === category)?.name || 'General Knowledge';

  return (
    <div className="game-card">
      <h1>
        <span role="img" aria-label="users">👥</span> Lobby: 
        <span 
          className={`lobby-code-clickable ${copySuccess ? 'copied' : ''}`}
          onClick={handleCopyLobbyCode}
          title="Click to copy lobby code"
        >
          {lobbyCode}
        </span>
      </h1>
      <p className="lobby-code-hint">
        <small>💡 Click the lobby code above to copy it and share with friends!</small>
      </p>
      
      <div className="lobby-info">
        <div className="lobby-settings-display">
          <div className="setting-badge">
            <strong>Category:</strong> {categoryName}
          </div>
          <div className="setting-badge">
            <strong>Difficulty:</strong> {difficulty?.toUpperCase() || 'MEDIUM'}
          </div>
          <div className="setting-badge">
            <strong>Questions:</strong> {questionCount}
          </div>
        </div>
        
        <div className="players-list">
          <div className="players-header">
            <h3>Players ({players?.length || 0})</h3>
            <div className="refresh-indicator">
              <span className="refresh-icon">🔄</span>
              <span className="refresh-text">
                Auto-refreshing every 2s
              </span>
            </div>
          </div>
          <div className="players-grid">
            {players?.map((player, index) => (
              <div key={player.userId || index} className="player-card">
                <span className="player-name">{player.playerName}</span>
                {index === 0 && <span className="host-badge">👑 Host</span>}
              </div>
            ))}
          </div>
        </div>
      </div>
      
      <div className="button-group">
        <button 
          className="back-button" 
          onClick={onLeaveLobby}
        >
          ← Leave Lobby
        </button>
        
        {isHost && (
          <button 
            className="start-button" 
            onClick={handleStartGame}
            disabled={loading || players?.length < 1}
          >
            {loading ? 'Starting...' : 'Start Game'}
          </button>
        )}
      </div>
      
      {error && <div className="error">{error}</div>}
    </div>
  );
};

export default LobbyWaitingRoom;
